/// <amd-module name="typeahead/features/FullscreenTypeahead" />
/// <amd-dependency path="jquery" name="$"/>

import {Experiment} from "typeahead/core/Experiment";
import {IExperimentalFeature} from "typeahead/core/IExperimentalFeature";
import {SuggestionListSelectedMessage} from "typeahead/core/message/SuggestionListSelectedMessage";
import {SuggestionSelectedMessage} from "typeahead/core/message/SuggestionSelectedMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import * as ExecutionHelper from "typeahead/core/plugins/ExecutionHelper";
import {IInputElementFocusByHumanOrScriptHandler}
    from "typeahead/core/presentation/IInputElementFocusByHumanOrScriptHandler";
import * as StripingConfiguration from "typeahead/core/StripingConfiguration";
import {IStripingConfiguration} from "typeahead/core/StripingConfiguration";
import {TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

const JP_SITE_ID: string = "28";

const popularDestConfigJP: IStripingConfiguration[] = [
    {
        clients: ["AWSHomepage", "Homepage"],
        experiment: {
            id: 25450,
            name: "ess_Recommendations_of_popular_destinations_at_Focus_jaJP_Hotel",
        },
        locales: ["ja_JP"],
        siteids: [JP_SITE_ID],
        tabs: ["hotel"],
    },
    {
        clients: ["HotelsLandingPage"],
        experiment: {
            id: 25450,
            name: "ess_Recommendations_of_popular_destinations_at_Focus_jaJP_Hotel",
        },
        locales: ["ja_JP"],
        packageTypes: [undefined],
        siteids: [JP_SITE_ID],
    },
    {
        clients: ["AWSHomepage", "Homepage"],
        experiment: {
            id: 25452,
            name: "ess_Recommendations_of_popular_destinations_at_Focus_jaJP_Flights",
        },
        locales: ["ja_JP"],
        siteids: [JP_SITE_ID],
        tabs: ["flight"],
    },
    {
        clients: ["FlightsLandingPage"],
        experiment: {
            id: 25452,
            name: "ess_Recommendations_of_popular_destinations_at_Focus_jaJP_Flights",
        },
        locales: ["ja_JP"],
        packageTypes: [undefined],
        siteids: [JP_SITE_ID],
    },
    {
        clients: ["Homepage", "AWSHomepage"],
        experiment: {
            id: 25453,
            name: "ess_Recommendations_of_popular_destinations_at_Focus_jaJP_Packages",
        },
        locales: ["ja_JP"],
        packageTypes: ["FH"],
        siteids: [JP_SITE_ID],
        tabs: ["package"],
    },
    {
        clients: [
            // Commented out until https://jira.expedia.biz/browse/ECOMSVC-3195 is fixed.
            // "HotelsLandingPage",
            // "FlightsLandingPage",
            "PackageLandingPage",
        ],
        experiment: {
          id: 25453,
          name: "ess_Recommendations_of_popular_destinations_at_Focus_jaJP_Packages",
        },
        locales: ["ja_JP"],
        packageTypes: ["FH"],
        siteids: [JP_SITE_ID],
    },
    {
        clients: ["AWSHomepage", "Homepage"],
        experiment: {
            id: 25451,
            name: "ess_Recommendations_of_popular_destinations_at_Focus_jaJP_LX",
        },
        locales: ["ja_JP"],
        siteids: [JP_SITE_ID],
        tabs: ["activity"],
    },
    {
        clients: ["LXLandingPage"],
        experiment: {
            id: 25451,
            name: "ess_Recommendations_of_popular_destinations_at_Focus_jaJP_LX",
        },
        locales: ["ja_JP"],
        siteids: [JP_SITE_ID],
    },
];

const popularDestinationsConfig: IStripingConfiguration[] = [
    {
        clients: ["Homepage", "AWSHomepage"],
        experiment: {
            id: 25284,
            name: "ess_Recommendations_of_popular_destinations_at_Focus",
        },
        tabs: ["hotel"],
    },
    {
        clients: ["HotelsLandingPage"],
        experiment: {
            id: 25284,
            name: "ess_Recommendations_of_popular_destinations_at_Focus",
        },
        packageTypes: [undefined],
    },
    {
        clients: ["Homepage", "AWSHomepage"],
        experiment: {
            id: 25331,
            name: "Ess_Recommendations_of_popular_destinations_at_Focus_Flights",
        },
        tabs: ["flight"],
    },
    {
        clients: ["FlightsLandingPage"],
        experiment: {
            id: 25331,
            name: "Ess_Recommendations_of_popular_destinations_at_Focus_Flights",
        },
        packageTypes: [undefined],
    },
    {
        clients: ["Homepage", "AWSHomepage"],
        experiment: {
            id: 25332,
            name: "Ess_Recommendations_of_popular_destinations_at_Focus_Packages",
        },
        packageTypes: ["FH"],
        tabs: ["package"],
    },
    {
        clients: ["HotelsLandingPage", "FlightsLandingPage"],
        experiment: {
            id: 25332,
            name: "Ess_Recommendations_of_popular_destinations_at_Focus_Packages",
        },
        packageTypes: ["FH"],
    },
    {
        clients: ["PackageLandingPage"],
        experiment: {
            id: 25332,
            name: "Ess_Recommendations_of_popular_destinations_at_Focus_Packages",
        },
        packageTypes: [
            "FH",
        ],
    },
    {
        clients: ["Homepage", "AWSHomepage"],
        experiment: {
            id: 25330,
            name: "Ess_Recommendations_of_popular_destinations_at_Focus_LX",
        },
        tabs: ["activity"],
    },
    {
        clients: ["LXLandingPage"],
        experiment: {
            id: 25330,
            name: "Ess_Recommendations_of_popular_destinations_at_Focus_LX",
        },
    },
];

const allConfigs = [popularDestinationsConfig, popularDestConfigJP];

const LOCALE_JA_JP: string = "ja_JP";

export class FullscreenTypeahead implements IExperimentalFeature, IInputElementFocusByHumanOrScriptHandler,
    IPageLoadHandler, IRequestUrlParamModifier, ISuggestionsModifier {

    private static cancelButtonHtml = `
        <button type="button" class="typeahead-overlay-cancel"><span class="uitk-icon">
        <svg role="img" aria-labelledby="icon-uitk-typeahead-cancel-title" viewBox="0 0 24 24" 
         style="fill:#616161; height:1.4em; width:1.4em;" xmlns="http://www.w3.org/2000/svg">
        <title id="icon-uitk-typeahead-cancel-title">Cancel</title> 
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"></path></svg></span></button>`;

    private static css = `
            .typeahead-overlay {
                background: rgba(245,245,245,1); 
                margin: 0;
                overflow: hidden !important; 
                padding: 0 !important;
                position: fixed !important;
                top: 0; right: 0; bottom: 0; left: 0;
                transition: top 0.3s;
                -moz-transition: top 0.3s;
                -webkit-transition: top 0.3s;                
                z-index: 99;
            }
            
            .typeahead-overlay-cancel {
                background-color: white;
                border:0;
                height: 52px;
                margin:0;
                padding-top: 6px;
                position: absolute;
                top: 0;
                width: 48px;
            }
            
            .typeahead-overlay input {       
                border: 0 !important;
                border-radius: 0 !important;   
                box-shadow: none !important;    
                height: 52px !important;               
                padding-left: 48px !important;
                padding-right: 48px !important;                
            }
            
            .typeahead-overlay label {     
                box-shadow: 0 2px 0.25rem 0 rgba(0,0,0,0.2) !important;         
                margin: 0;                
            }                        
            
            .typeahead-overlay button.btn-clear {
                height: 52px;
                margin-top: -12px;                
                right: 0;
                width: 48px;
            }                         
            
            .typeahead-overlay .autocomplete-open .autocomplete-arrow::after,
            .typeahead-overlay label span.label, 
            .typeahead-overlay label span.icon {
                display: none;
            }   
            
            .typeahead-overlay .autocomplete-dropdown {
                background: none;
                border: 0;
                box-shadow: none;
                height: -moz-calc(100% - 52px);
                height: -webkit-calc(100% - 52px);
                height: calc(100% - 52px);
                left:0 !important;
                margin: 0;   
                max-width: 100% !important;
                overflow:auto;    
                right: 0 !important;
                width: 100% !important;                                                          
            }    
            
            .typeahead-overlay a.details { min-height: 32px; }
            .typeahead-overlay .footer { display: none; }   
            .typeahead-overlay .autocomplete-open { height: 100%; }            
            .scroll-hidden { overflow: hidden; position: fixed; }                  
            .typeahead-overlay .label.show { display: block; left: 48px; }          
            .typeahead-overlay .btn-clear:disabled { display:none; }   		
    		.typeahead-overlay input:placeholder-shown { padding-right:18px !important; }            
            .typeahead-overlay input::-webkit-input-placeholder { font-size: 14px; }
            .typeahead-overlay input::-moz-placeholder { font-size: 14px; }
            .typeahead-overlay input:-ms-input-placeholder { font-size: 14px; }
            .typeahead-overlay input::placeholder { font-size: 14px; } 
            .typeahead-overlay ul.results { padding-top: 2px; }       
            .typeahead-overlay div.multiLineDisplay { display: inline-block; }                           
        `;

    private static isOpen($typeaheadContainer: JQuery): boolean {
        return $typeaheadContainer.hasClass("typeahead-overlay");
    }

    private static addCancelButton($typeaheadContainer: JQuery): JQuery {
        let cancelButton: JQuery = $.parseHTML(FullscreenTypeahead.cancelButtonHtml).pop();
        $typeaheadContainer.append(cancelButton);
        $(cancelButton).off("mousedown").on("mousedown", (event) => {
            event.stopPropagation();
            window.history.back();
        });
        // Disable parent typeahead input uitk event handlers
        $(cancelButton).off("click focusin").on("click focusin", (event) => { event.stopPropagation(); });
        return cancelButton;
    }

    private static applyFullScreenStyle($typeaheadContainer: JQuery): void {
        $typeaheadContainer.addClass("typeahead-overlay");
        $("body").addClass("scroll-hidden");
        // Show labels if they are embedded withing input box in storefront
        if ($(".typeahead-overlay span.label").css("position") === "absolute") {
            $(".typeahead-overlay span.label").addClass("show");
        }
    }

    private static close($inputElement: JQuery, defaultPlaceHolder: string, cancelButton: JQuery,
                         suggestionSelectedSubscription: string): void {
        setTimeout(() => {
            let $typeaheadContainer = $($inputElement).closest("div.col");
            $($inputElement).attr("placeholder", defaultPlaceHolder);
            $("body").removeClass("scroll-hidden");
            FullscreenTypeahead.enableOtherInputs($inputElement);
            $(cancelButton).remove();
            $typeaheadContainer.removeClass("typeahead-overlay");
            TypeaheadMessage.unsubscribe(SuggestionSelectedMessage.getTopic(), suggestionSelectedSubscription);
            $($inputElement).trigger("blur");
            window.uitkautocomplete.prototype.closeAllTA();
            $inputElement.off("mousedown.fullscreentypeahead keydown.fullscreentypeahead keyup.fullscreentypeahead");
        }, 0); // To fix click going through to the page behind
    }

    private static open($inputElement): void {
        let $typeaheadContainer = $($inputElement).closest("div.col");
        let typeaheadId: number;
        let defaultPlaceHolder: string = $($inputElement).attr("placeholder");
        if (!FullscreenTypeahead.isOpen($typeaheadContainer)) {
            typeaheadId = Date.now();
            history.pushState({placeholder: defaultPlaceHolder}, document.title, "?taid=" + typeaheadId +
                "&tapage=1&next=popular");
            FullscreenTypeahead.disableOtherInputs($inputElement);
            let cancelButton: JQuery = FullscreenTypeahead.addCancelButton($typeaheadContainer);
            FullscreenTypeahead.applyFullScreenStyle($typeaheadContainer);
            let suggestionSelectedSubscription = TypeaheadMessage.subscribe(SuggestionSelectedMessage.getTopic(),
                () => {
                    window.history.go(- parseInt(TypeaheadUtils.getURLParameter("tapage"), 10));
                });
            $(window).off("popstate.typeahead").on("popstate.typeahead", (event: JQueryEventObject) => {
                if (location.search.indexOf("taid=" + typeaheadId) > -1) {
                    $inputElement.trigger("focus", true);
                    let popStateEvent: PopStateEvent = <PopStateEvent> (event.originalEvent);
                    if (popStateEvent && popStateEvent.state && popStateEvent.state.placeholder) {
                        $inputElement.attr("placeholder", popStateEvent.state.placeholder);
                    }
                } else {
                    FullscreenTypeahead.close($inputElement, defaultPlaceHolder, cancelButton,
                        suggestionSelectedSubscription);
                    typeaheadId = 0;
                }
            });
            // Fix Fullscreen typeahead input element scrolling up and outside of view in iOS
            $("html, body").animate({ scrollTop: 0, scrollLeft: 0 }, "slow");
        }
    }

    private static disableOtherInputs($inputElement: JQuery): void {
        $($inputElement).closest("form").
        find("input:visible, select:visible").not($inputElement).attr("disabled", "true");
    }

    private static enableOtherInputs($inputElement: JQuery): void {
        $($inputElement).closest("form").
        find("input:visible, select:visible").not($inputElement).removeAttr("disabled");
    }

    private static shouldExecute(context: IContext): boolean {
        return context.siteid === JP_SITE_ID
            ? ExecutionHelper.shouldExecute(context, popularDestConfigJP)
            : ExecutionHelper.shouldExecute(context, popularDestinationsConfig);
    }

    // Checks for popular destination experiments only
    private static isMultiStep(context: IContext, options: any) {
        return !options.srchText && TypeaheadUtils.isWithinMobileBreakpoint() && this.shouldExecute(context);
    }

    private static handleSuggestionListSelection(topic: string, message: SuggestionListSelectedMessage): void {
        let nextParameter = "next=" + message.getSuggestion().getNext().orElse("");
        let taPageParameter = "tapage=" + (parseInt(TypeaheadUtils.getURLParameter("tapage"), 10) + 1);
        let taIdParameter = "taid=" + TypeaheadUtils.getURLParameter("taid");
        history.pushState({placeholder: message.getSuggestion().getDisplayText()}, document.title,
            "?" + taIdParameter + "&" + taPageParameter + "&" + nextParameter + "&depth=" +
            message.getSuggestion().getPosition());
        let $input = $("#" + message.getDomElementId());
        $input.attr("placeholder", message.getSuggestion().getDisplayText());
        $input.trigger("focus", true);
    }

    public handlePageLoad(options: any, context: IContext): void {
        let style = document.createElement("style");
        style.type = "text/css";
        style.innerHTML = FullscreenTypeahead.css;
        document.head.appendChild(style);
        // Reset storefront url if stale parameters are present
        if (location.search.indexOf("taid=") > -1) {
            history.pushState(null, document.title, location.pathname);
        }
        TypeaheadMessage.subscribe(SuggestionListSelectedMessage.getTopic(),
            FullscreenTypeahead.handleSuggestionListSelection);
    }

    public handleInputElementFocusByHumanOrScript($inputElement: JQuery, context: IContext): void {
        if (TypeaheadUtils.isWithinMobileBreakpoint() && FullscreenTypeahead.shouldExecute(context)) {
            FullscreenTypeahead.open($inputElement);
            // Hide ja_JP typeahead
            $("div.dropdown-con").css("display", "none");
            $inputElement.on("mousedown.fullscreentypeahead keydown.fullscreentypeahead keyup.fullscreentypeahead",
            () => {
                $("div.dropdown-con").css("display", "none");
            });
        }
    }

    public modifyParameters(params: ISuggestServiceQueryParams, context: IContext, options: any):
            ISuggestServiceQueryParams {
        if (FullscreenTypeahead.isMultiStep(context, options)) {
            params.next = TypeaheadUtils.getURLParameter("next");

            // get the experiment bucket for JP and non jp locales
            let evaluatedExperiment: Experiment;
            if (context.siteid !== JP_SITE_ID) {
                evaluatedExperiment =
                    ExecutionHelper.doNotCallThisToGetEvaluatedExperimentInfo(context, popularDestinationsConfig);
            } else if (context.locale === "ja_JP") {
                evaluatedExperiment =
                  ExecutionHelper.doNotCallThisToGetEvaluatedExperimentInfo(context, popularDestConfigJP);
            }
            if (evaluatedExperiment) {
                params.ab = evaluatedExperiment.id + "." + evaluatedExperiment.value;
            }

            let isNextDomestic: boolean = ((params.next + "").toLowerCase().indexOf("domestic") > -1);
            let isNextInternational: boolean = ((params.next + "").toLowerCase().indexOf("international") > -1);
            let isOriginInput: boolean = ($("input:focus").data("gcw-storeable-name") &&
                    $("input:focus").data("gcw-storeable-name").indexOf("origin") > -1);

            if (context.locale === LOCALE_JA_JP && (isNextDomestic || isNextInternational)) {
                params.maxresults = (isOriginInput && isNextDomestic) ? 15 : 18;
            }
        }
        return params;
    }

    public modifySuggestions(suggestions: any, context: IContext, options: any): Object[] {
        if (FullscreenTypeahead.isMultiStep(context, options)) {
            let parentPosition: string = TypeaheadUtils.getURLParameter("depth");
            suggestions.forEach((sugg): void => {
                sugg.parentPosition = parentPosition;
            });
        }
        return suggestions;
    }

    public getExperiments(): Experiment[] {
        return StripingConfiguration.getExperiments([].concat.apply([], allConfigs));
    }
}
